import { z } from "zod";
import {
  BUSINESS_VERTICALS,
  type BusinessVerticalId,
} from "@/lib/business-verticals";

/**
 * Per-tenant public content packet. Schema only — no loader, no routes, no
 * starter file. Each top-level key is a future Convex table. Values are
 * sourced facts, not platform brand tokens.
 */

export const TODO_OWNER_INPUT = "TODO_OWNER_INPUT" as const;

export const SITE_PACKET_SECTION_KEYS = [
  "tenant",
  "brand",
  "locations",
  "menu",
  "about",
  "gallery",
  "catering",
  "privateEvents",
  "events",
  "legal",
  "images",
  "sources",
] as const;

export type SitePacketSectionKey = (typeof SITE_PACKET_SECTION_KEYS)[number];

export const SITE_PACKET_VERTICAL_IDS = Object.keys(BUSINESS_VERTICALS) as [
  BusinessVerticalId,
  ...BusinessVerticalId[],
];

const sourcedTextSchema = z.union([
  z.object({
    value: z.string(),
    sourceRef: z.string().min(1),
  }),
  z.literal(TODO_OWNER_INPUT),
]);

type SourcedText = z.infer<typeof sourcedTextSchema>;

const weekdaySchema = z.enum([
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
]);

const locationHoursSchema = z.object({
  weekday: weekdaySchema,
  opens: z.string(),
  closes: z.string(),
});

const locationSchema = z.object({
  name: sourcedTextSchema,
  streetAddress: sourcedTextSchema,
  city: sourcedTextSchema,
  region: sourcedTextSchema,
  postalCode: sourcedTextSchema,
  phone: sourcedTextSchema,
  mapLink: sourcedTextSchema,
  hours: z.array(locationHoursSchema),
  hoursSourceRef: z.string().min(1),
});

const menuItemSchema = z.object({
  name: sourcedTextSchema,
  description: sourcedTextSchema,
  price: z.string(),
  currency: z.string().min(1),
  dietaryTags: z.array(z.string()),
  sourceRef: z.string().min(1),
});

const menuSectionSchema = z.object({
  name: sourcedTextSchema,
  items: z.array(menuItemSchema),
});

const menuSchema = z.object({
  scrapedAt: z.string().min(1),
  sourceRefs: z.array(z.string().min(1)),
  sections: z.array(menuSectionSchema),
});

const copyBlockSchema = z.object({
  headline: sourcedTextSchema,
  body: sourcedTextSchema,
});

const galleryItemSchema = z.object({
  caption: sourcedTextSchema,
  imageSlot: z.string().min(1),
});

const eventItemSchema = z.object({
  title: sourcedTextSchema,
  description: sourcedTextSchema,
  when: sourcedTextSchema,
});

const imageSchema = z.object({
  slot: z.string().min(1),
  path: z.string().min(1),
  source: z.union([z.string().min(1), z.literal("generated")]),
  rights: sourcedTextSchema,
  notes: sourcedTextSchema,
});

const sourceRecordSchema = z.object({
  id: z.string().min(1).optional(),
  url: z.string().min(1),
  fetchedAt: z.string().min(1),
});

function pushSourcedRef(refs: string[], value: SourcedText): void {
  if (value !== TODO_OWNER_INPUT) {
    refs.push(value.sourceRef);
  }
}

function pushCopyBlockRefs(
  refs: string[],
  block: { headline: SourcedText; body: SourcedText },
): void {
  pushSourcedRef(refs, block.headline);
  pushSourcedRef(refs, block.body);
}

function collectSourceRefs(packet: {
  brand: { name: SourcedText; tagline: SourcedText };
  locations: Array<{
    name: SourcedText;
    streetAddress: SourcedText;
    city: SourcedText;
    region: SourcedText;
    postalCode: SourcedText;
    phone: SourcedText;
    mapLink: SourcedText;
    hoursSourceRef: string;
  }>;
  menu: {
    sourceRefs: string[];
    sections: Array<{
      name: SourcedText;
      items: Array<{
        name: SourcedText;
        description: SourcedText;
        sourceRef: string;
      }>;
    }>;
  };
  about: { headline: SourcedText; body: SourcedText };
  gallery: Array<{ caption: SourcedText }>;
  catering: { headline: SourcedText; body: SourcedText };
  privateEvents: { headline: SourcedText; body: SourcedText };
  events: Array<{
    title: SourcedText;
    description: SourcedText;
    when: SourcedText;
  }>;
  legal: { privacy: SourcedText; terms: SourcedText };
  images: Array<{ rights: SourcedText; notes: SourcedText }>;
}): string[] {
  const refs: string[] = [];
  pushSourcedRef(refs, packet.brand.name);
  pushSourcedRef(refs, packet.brand.tagline);
  for (const location of packet.locations) {
    pushSourcedRef(refs, location.name);
    pushSourcedRef(refs, location.streetAddress);
    pushSourcedRef(refs, location.city);
    pushSourcedRef(refs, location.region);
    pushSourcedRef(refs, location.postalCode);
    pushSourcedRef(refs, location.phone);
    pushSourcedRef(refs, location.mapLink);
    refs.push(location.hoursSourceRef);
  }
  refs.push(...packet.menu.sourceRefs);
  for (const section of packet.menu.sections) {
    pushSourcedRef(refs, section.name);
    for (const item of section.items) {
      pushSourcedRef(refs, item.name);
      pushSourcedRef(refs, item.description);
      refs.push(item.sourceRef);
    }
  }
  pushCopyBlockRefs(refs, packet.about);
  for (const item of packet.gallery) {
    pushSourcedRef(refs, item.caption);
  }
  pushCopyBlockRefs(refs, packet.catering);
  pushCopyBlockRefs(refs, packet.privateEvents);
  for (const event of packet.events) {
    pushSourcedRef(refs, event.title);
    pushSourcedRef(refs, event.description);
    pushSourcedRef(refs, event.when);
  }
  pushSourcedRef(refs, packet.legal.privacy);
  pushSourcedRef(refs, packet.legal.terms);
  for (const image of packet.images) {
    pushSourcedRef(refs, image.rights);
    pushSourcedRef(refs, image.notes);
  }
  return refs;
}

function sourceLookupKeys(
  sources: Array<{ id?: string; url: string }>,
): Set<string> {
  const keys = new Set<string>();
  for (const source of sources) {
    if (source.id) {
      keys.add(source.id);
    }
    keys.add(source.url);
  }
  return keys;
}

export const sitePacketSchema = z
  .strictObject({
    tenant: z.object({
      slug: z.string().min(1),
      displayName: z.string().min(1),
      vertical: z.enum(SITE_PACKET_VERTICAL_IDS),
      packetVersion: z.number().int().positive(),
      draft: z.boolean(),
    }),
    brand: z.object({
      name: sourcedTextSchema,
      tagline: sourcedTextSchema,
      paletteHints: z.array(z.string()),
    }),
    locations: z.array(locationSchema),
    menu: menuSchema,
    about: copyBlockSchema,
    gallery: z.array(galleryItemSchema),
    catering: copyBlockSchema,
    privateEvents: copyBlockSchema,
    events: z.array(eventItemSchema),
    legal: z.object({
      privacy: sourcedTextSchema,
      terms: sourcedTextSchema,
    }),
    images: z.array(imageSchema),
    sources: z.array(sourceRecordSchema),
  })
  .superRefine((packet, ctx) => {
    const keys = sourceLookupKeys(packet.sources);
    for (const ref of collectSourceRefs(packet)) {
      if (!keys.has(ref)) {
        ctx.addIssue({
          code: "custom",
          message: `sourceRef "${ref}" does not resolve to a sources[] id or url`,
        });
      }
    }
  });

export type { SourcedText };
export type SitePacket = z.infer<typeof sitePacketSchema>;

export function parseSitePacket(input: unknown): SitePacket {
  const result = sitePacketSchema.safeParse(input);
  if (!result.success) {
    throw new Error(`Invalid site packet: ${result.error.message}`);
  }
  return result.data;
}
